import * as React from 'react';
import { Canceler } from 'axios';
import { groupBy } from 'lodash';

import { IParameter } from './types';
import { ITableRowValue } from '../components/TableRow';
import { IParametersProps, IParametersState } from './types';
import { IItem } from '../Items/types';

import { MarketContext } from 'src/services/contexts/markets/MarketContext';
import { listExceptions } from 'src/views/Admin/services/listExceptions';
import New from './components/New';
import Edit from './components/Edit';
import API from 'src/services/api';
import Subpage from '../components/Subpage';

export default class Parameters extends React.Component<
  IParametersProps,
  IParametersState
> {
  static contextType = MarketContext;
  context!: React.ContextType<typeof MarketContext>;
  parameterOptions: any[];
  reqCancelers: Canceler[] = [];

  state: IParametersState = {
    parameters: [],
    archivedParameters: [],
    itemOptionsWithParameter: [],
    marketOptions: [],
    isLoading: true,
  };

  async componentDidMount() {
    const requests = [API.ADMIN.PARAMETERS.getAll(), API.ADMIN.ITEMS.getAll()];
    this.reqCancelers = requests.map((req) => req.cancel);
    const [allParameters, items] = await Promise.all(
      requests.map((req) => req.getData()) as [
        Promise<IParameter[]>,
        Promise<IItem[]>
      ]
    );
    this.reqCancelers = [];

    const markets = this.context.localContentAdminProfiles;

    const itemOptionsWithParameter = items.map((item) => ({
      parameterId: item.parameter.id,
      option: {
        label: item.name,
        value: {
          id: item.id,
          name: item.name,
          isDeleted: item.isDeleted,
        },
      },
    }));
    const marketOptions = markets.map((market) => ({
      label: market.name,
      value: market,
    }));
    const { parameters, archivedParameters } = groupBy(allParameters, (item) =>
      item.isDeleted ? 'archivedParameters' : 'parameters'
    );
    this.setState(
      {
        parameters: parameters || [],
        archivedParameters: archivedParameters || [],
        itemOptionsWithParameter,
        marketOptions,
        isLoading: false,
      },
      this.updateParameterOptions
    );
  }

  componentWillUnmount() {
    this.reqCancelers.forEach((cancel) => cancel());
  }

  columns: ITableRowValue[] = [
    'Name',
    [
      'Parameter Visibility',
      'Select here if a parameter should always be visible or always be hidden in the "Create New Links - Individual Links" interface. Should it always be shown or only when a specific value has been selected before?',
    ],
    [
      'Exceptions (Parameter)',
      'Select here if there are any exceptions to the visibility default you have selected for the parameter before (-> always visible / always hidden).',
    ],
    [
      'Profile Visibility',
      'Select here if a parameter should always be visible or always be hidden as a default for all available profiles in the "Create New Links - Individual Links" interface.',
    ],
    [
      'Exceptions (Profiles)',
      'Select here if there are any exceptions to the visibility default you have selected for the profile(s) before (-> always visible / always hidden in profiles).',
    ],
    [
      'Parameter Order',
      'Select here the order of when each parameter will appear in the "Create New Links - Individual Link" interface. Please only use numbers.',
    ],
  ];

  getColumnValues = (parameter: IParameter) => [
    `${parameter.name} (${parameter.id})`,
    parameter.defaultItems ? 'Visible' : 'Hidden',
    listExceptions(parameter.parameterVisibilityConditions, 'item'),
    parameter.defaultProfiles ? 'Visible' : 'Hidden',
    listExceptions(parameter.parameterProfileVisibilityConditions, 'profile'),
    String(parameter.orderRender),
  ];

  getSearchString = (parameter: IParameter) =>
    `${parameter.name} (${parameter.id})`;

  updateParameterOptions = () => {
    this.parameterOptions = this.state.parameters.map(
      ({ name, id, parameterGroup }) => ({
        label: name,
        value: {
          name,
          id,
          parameterGroup,
        },
      })
    );
  };

  add = async (item: IParameter) => {
    const newItem = await API.ADMIN.PARAMETERS.add(item);
    this.setState(
      ({ parameters }) => ({
        parameters: [newItem, ...parameters],
      }),
      this.updateParameterOptions
    );
  };

  update = async (item: IParameter) => {
    const updatedItem = await API.ADMIN.PARAMETERS.update(item);
    this.setState(({ parameters }) => {
      const userIndex = parameters.findIndex((el) => el.id === updatedItem.id);
      return {
        parameters: [
          ...parameters.slice(0, userIndex),
          updatedItem,
          ...parameters.slice(userIndex + 1),
        ],
      };
    }, this.updateParameterOptions);
  };

  toggleArchive = (item: IParameter) => () => {
    const isArchived = item.isDeleted;
    const updatedItem = {
      ...item,
      isDeleted: !isArchived,
    };
    API.ADMIN.PARAMETERS.update(updatedItem);
    this.setState(
      (state) => ({
        parameters: isArchived
          ? [...state.parameters, updatedItem]
          : state.parameters.filter((el) => el.id !== item.id),
        archivedParameters: !isArchived
          ? [...state.archivedParameters, updatedItem]
          : state.archivedParameters.filter((el) => el.id !== item.id),
      }),
      this.updateParameterOptions
    );
  };

  NewForm = (props: any) => (
    <New
      add={this.add}
      parameterOptions={this.parameterOptions}
      itemOptionsWithParameter={this.state.itemOptionsWithParameter}
      marketOptions={this.state.marketOptions}
      {...props}
    />
  );

  EditForm = (props: any) => (
    <Edit
      parameterOptions={this.parameterOptions}
      itemOptionsWithParameter={this.state.itemOptionsWithParameter}
      marketOptions={this.state.marketOptions}
      update={this.update}
      onToggleArchive={this.toggleArchive(props.item)}
      {...props}
    />
  );

  render() {
    return (
      <Subpage
        currentPath={this.props.location.pathname}
        NewForm={this.NewForm}
        EditForm={this.EditForm}
        columns={this.columns}
        getColumnValues={this.getColumnValues}
        getSearchString={this.getSearchString}
        items={this.state.parameters}
        archivedItems={this.state.archivedParameters}
        isLoading={this.state.isLoading}
        hasArchive={true}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/Admin/Parameters/Parameters.tsx